import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { InfoCircleFilled } from "@ant-design/icons";
import { DatePicker, Form, Image, Input, Popover, Radio, Space, Table, Tag } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ETechnicalDefinitionType } from "@devowl-wp/api-real-cookie-banner";
import { useServiceTemplateTechnicalDefinitionsTableColumnsReadOnly } from "./columns.js";
import { getServiceTemplateTechnicalDefinitionTypes } from "./useDefinitionTypes.js";
import { isCookieRawDataSuitable, isStorageItemRawDataSuitable, isValidHostByType, parseHttpCookies, parseStorageItems } from "./util.js";
import helpCookieParserCookieRaw from "../../../../../../../assets/help/cookie-parser/cookie-raw.png";
import helpCookieParserLocalRaw from "../../../../../../../assets/help/cookie-parser/local-raw.png";
import helpCookieParserSessionRaw from "../../../../../../../assets/help/cookie-parser/session-raw.png";
import helpCookieParserSiteHost from "../../../../../../../assets/help/cookie-parser/site-tld.png";
import helpCookieParserStorageItemOriginLocal from "../../../../../../../assets/help/cookie-parser/storage-item-origin-local.png";
import helpCookieParserStorageItemOriginSession from "../../../../../../../assets/help/cookie-parser/storage-item-origin-session.png";
import { useTranslation } from "../../../../../../../others/i18n.js";
import { colors } from "../../../../../../../styles/colors.js";
import { normalizeUrlWithProtocol } from "../../../../../../../util/template/url.js";
function CookieParserForm(props) {
    const { hidden, form } = props;
    const { t } = useTranslation();
    const technicalDefinitionTypes = getServiceTemplateTechnicalDefinitionTypes(t).filter((type)=>type.value !== ETechnicalDefinitionType.IndexedDb);
    const technicalDefinitionTypeName = "technicalDefinitionType";
    const siteHost = "siteHost";
    const origin = "origin";
    const rawTechnicalDefinitionsName = "rawTechnicalDefinitions";
    const parsedDataName = "parsedData";
    const cookieCreated = "cookieCreated";
    const [httpCookiesParsed, setHttpCookiesParsed] = useState();
    const [storageItemsParsed, setStorageItemsParsed] = useState();
    const alwaysHidden = [
        "purpose"
    ];
    const storageItemsHidden = [
        ...alwaysHidden,
        "duration"
    ];
    const [hiddenColumns, setHiddenColumns] = useState(alwaysHidden);
    const handleCookieParserFormData = useCallback(()=>{
        const rawString = form.getFieldValue([
            rawTechnicalDefinitionsName
        ]);
        const type = form.getFieldValue([
            technicalDefinitionTypeName
        ]);
        const originHost = form.getFieldValue([
            origin
        ]);
        const siteHostValue = form.getFieldValue([
            siteHost
        ]);
        const cookieCreatedValue = form.getFieldValue([
            cookieCreated
        ]);
        if (type) {
            switch(type){
                case ETechnicalDefinitionType.Http:
                    setHttpCookiesParsed(parseHttpCookies(rawString, siteHostValue, cookieCreatedValue ? cookieCreatedValue.toDate() : undefined));
                    setHiddenColumns(alwaysHidden);
                    break;
                default:
                    setStorageItemsParsed(parseStorageItems(rawString, type, originHost, siteHostValue));
                    setHiddenColumns(storageItemsHidden);
                    break;
            }
        }
    }, []);
    const [previewData, setPreviewData] = useState([]);
    const [summary, setSummary] = useState();
    const [invalidRawData, setInvalidRawData] = useState(false);
    useEffect(()=>{
        // if something could be parsed update the preview table data
        const technicalDefinitions = [];
        const type = form.getFieldValue([
            technicalDefinitionTypeName
        ]);
        if (storageItemsParsed || httpCookiesParsed) {
            // use form to add them
            // Also collect import summary
            const summaries = {
                insertedHttp: 0,
                skippedHttp: [],
                insertedLocal: 0,
                skippedLocal: [],
                insertedSession: 0,
                skippedSession: []
            };
            // Check http cookies for primary key (http type + name + host) to update duration!
            // Status Badge: Modified
            if (httpCookiesParsed && type === ETechnicalDefinitionType.Http) {
                for (const cookie of httpCookiesParsed){
                    if (cookie.isKnownDefault) {
                        summaries.skippedHttp.push(cookie);
                    } else {
                        technicalDefinitions.push({
                            id: undefined,
                            orderPosition: technicalDefinitions.length,
                            type: ETechnicalDefinitionType.Http,
                            name: cookie.name,
                            host: cookie.host,
                            duration: cookie.duration,
                            durationUnit: cookie.durationUnit,
                            isSessionDuration: cookie.isSessionDuration
                        });
                        summaries.insertedHttp++;
                    }
                }
                setStorageItemsParsed(undefined); // reset as only one type is parsed at once
            }
            // Storage items should not be added twice (only missing ones)
            if (storageItemsParsed && [
                ETechnicalDefinitionType.Local,
                ETechnicalDefinitionType.Session
            ].includes(type)) {
                for (const item of storageItemsParsed){
                    if (item.isKnownDefault) {
                        if (item.type === ETechnicalDefinitionType.Local) {
                            summaries.skippedLocal.push(item);
                        } else if (item.type === ETechnicalDefinitionType.Session) {
                            summaries.skippedSession.push(item);
                        }
                    } else {
                        // add missing
                        technicalDefinitions.push({
                            id: undefined,
                            orderPosition: technicalDefinitions.length,
                            type: item.type,
                            name: item.key,
                            host: item.host,
                            duration: undefined,
                            durationUnit: undefined,
                            isSessionDuration: false
                        });
                        if (item.type === ETechnicalDefinitionType.Local) {
                            summaries.insertedLocal++;
                        } else if (item.type === ETechnicalDefinitionType.Session) {
                            summaries.insertedSession++;
                        }
                    }
                }
                setHttpCookiesParsed(undefined); // reset as only one type is parsed at once
            }
            setSummary(summaries);
            form.setFieldValue([
                parsedDataName
            ], technicalDefinitions);
        }
        setPreviewData(technicalDefinitions);
    }, [
        httpCookiesParsed,
        storageItemsParsed
    ]);
    const displayColumnDefinition = useMemo(()=>useServiceTemplateTechnicalDefinitionsTableColumnsReadOnly(t, hiddenColumns), [
        hiddenColumns
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        hidden: hidden,
        children: [
            /*#__PURE__*/ _jsx(Form.Item, {
                children: t("You can copy the cookie, local storage or session storage information in raw format from your Chromium based browser (preferably Google Chrome) and transform it into technical cookie information of the service template.")
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                name: cookieCreated,
                label: t("Cookies created"),
                extra: t("Please enter the cookies created in your browser so that we can calculate how long they will be stored (duration). This input uses your local time zone. However, the validity period of cookies in the Chromium developer tools is displayed in Coordinated Universal Time (UTC)."),
                rules: [
                    {
                        required: true,
                        message: t("Please give a proper cookie creation date.")
                    }
                ],
                required: true,
                children: /*#__PURE__*/ _jsx(DatePicker, {
                    showTime: true,
                    onChange: ()=>{
                        handleCookieParserFormData();
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                name: technicalDefinitionTypeName,
                label: "Cookie type",
                required: true,
                rules: [
                    {
                        required: true,
                        message: t("Please choose a cookie type. ")
                    }
                ],
                children: /*#__PURE__*/ _jsx(Radio.Group, {
                    optionType: "button",
                    options: technicalDefinitionTypes,
                    onChange: ()=>{
                        handleCookieParserFormData();
                    },
                    buttonStyle: "solid"
                })
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                shouldUpdate: (prev, next)=>prev.technicalDefinitionType !== next.technicalDefinitionType,
                noStyle: true,
                children: ({ getFieldValue })=>{
                    const type = getFieldValue([
                        technicalDefinitionTypeName
                    ]);
                    const isHidden = !type;
                    return /*#__PURE__*/ _jsx(Form.Item, {
                        hidden: isHidden,
                        dependencies: [
                            technicalDefinitionTypeName
                        ],
                        name: siteHost,
                        label: /*#__PURE__*/ _jsxs(Space, {
                            size: "small",
                            children: [
                                t("Site host (top level)"),
                                /*#__PURE__*/ _jsx(Popover, {
                                    content: /*#__PURE__*/ _jsx(Image, {
                                        src: helpCookieParserSiteHost,
                                        preview: false
                                    }),
                                    placement: "right",
                                    children: /*#__PURE__*/ _jsx(InfoCircleFilled, {
                                        style: {
                                            color: colors.ci.devowlBlue
                                        }
                                    })
                                })
                            ]
                        }),
                        extra: t("Hostname with a top level domain (TLD) or ccSLD like devowl.io or devowl.co.uk."),
                        required: true,
                        rules: [
                            {
                                validator: (_, value)=>{
                                    // Regex for host with tld and sld
                                    const pattern = /^(?!-)[a-zA-Z0-9-]{1,63}(?<!-)\.(?:[a-zA-Z]{2,11}|[a-zA-Z0-9-]{2,}\.[a-zA-Z]{2,11})$/;
                                    if (!value || pattern.test(value)) {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject(t("Please provide qualified host name like your-name.com"));
                                    }
                                }
                            },
                            {
                                required: !isHidden,
                                message: t("Please provide a qualified domain name")
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            placeholder: "devowl.io",
                            onChange: ()=>{
                                handleCookieParserFormData();
                            }
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                shouldUpdate: (prev, next)=>prev.technicalDefinitionType !== next.technicalDefinitionType,
                noStyle: true,
                children: ({ getFieldValue })=>{
                    const type = getFieldValue([
                        technicalDefinitionTypeName
                    ]);
                    const isHidden = ![
                        ETechnicalDefinitionType.Session,
                        ETechnicalDefinitionType.Local
                    ].includes(type);
                    return /*#__PURE__*/ _jsx(Form.Item, {
                        hidden: isHidden,
                        dependencies: [
                            technicalDefinitionTypeName
                        ],
                        name: origin,
                        label: /*#__PURE__*/ _jsxs(Space, {
                            size: "small",
                            children: [
                                t("Origin"),
                                /*#__PURE__*/ _jsx(Popover, {
                                    content: /*#__PURE__*/ _jsx(Image, {
                                        src: type === ETechnicalDefinitionType.Local ? helpCookieParserStorageItemOriginLocal : type === ETechnicalDefinitionType.Session ? helpCookieParserStorageItemOriginSession : undefined,
                                        preview: false
                                    }),
                                    placement: "right",
                                    children: /*#__PURE__*/ _jsx(InfoCircleFilled, {
                                        style: {
                                            color: colors.ci.devowlBlue
                                        }
                                    })
                                })
                            ]
                        }),
                        required: true,
                        rules: [
                            ()=>({
                                    validator: (_, host)=>{
                                        if (isHidden) {
                                            return Promise.resolve();
                                        }
                                        if (isValidHostByType(type, host)) {
                                            return Promise.resolve();
                                        } else {
                                            return Promise.reject(t("Please provide the technical origin!"));
                                        }
                                    }
                                })
                        ],
                        normalize: (value)=>normalizeUrlWithProtocol(value),
                        children: /*#__PURE__*/ _jsx(Input, {
                            placeholder: "https://devowl.io",
                            onBlur: ()=>{
                                handleCookieParserFormData();
                            }
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                shouldUpdate: (prev, next)=>prev.technicalDefinitionType !== next.technicalDefinitionType,
                noStyle: true,
                children: ({ getFieldValue })=>{
                    const type = getFieldValue([
                        technicalDefinitionTypeName
                    ]);
                    let helpImage = undefined;
                    switch(type){
                        case ETechnicalDefinitionType.Http:
                            helpImage = helpCookieParserCookieRaw;
                            break;
                        case ETechnicalDefinitionType.Local:
                            helpImage = helpCookieParserLocalRaw;
                            break;
                        case ETechnicalDefinitionType.Session:
                            helpImage = helpCookieParserSessionRaw;
                            break;
                        default:
                            break;
                    }
                    return /*#__PURE__*/ _jsx(Form.Item, {
                        hidden: type?.toString()?.length === 0,
                        dependencies: [
                            technicalDefinitionTypeName
                        ],
                        name: rawTechnicalDefinitionsName,
                        label: /*#__PURE__*/ _jsxs(Space, {
                            size: "small",
                            children: [
                                t("Technical definitions"),
                                /*#__PURE__*/ _jsx(Popover, {
                                    content: /*#__PURE__*/ _jsx(Image, {
                                        src: helpImage
                                    }),
                                    placement: "right",
                                    children: /*#__PURE__*/ _jsx(InfoCircleFilled, {
                                        style: {
                                            color: colors.ci.devowlBlue
                                        }
                                    })
                                })
                            ]
                        }),
                        extra: t("Click in the table of technical definitions in the browser select and copy table content with the mouse (does not work via keyboard)."),
                        rules: [
                            {
                                required: true,
                                min: 1,
                                message: t("Please enter the raw data copied from your browser.")
                            },
                            {
                                required: true,
                                validator: (_, value)=>{
                                    const type = getFieldValue(technicalDefinitionTypeName);
                                    switch(type){
                                        case ETechnicalDefinitionType.Http:
                                            if (!isCookieRawDataSuitable(value)) {
                                                setInvalidRawData(true);
                                                return Promise.reject(t("Please provide suitable http cookie data. Maybe you mean any kind of storage item type?"));
                                            }
                                            break;
                                        case ETechnicalDefinitionType.Local:
                                        case ETechnicalDefinitionType.Session:
                                            if (!isStorageItemRawDataSuitable(value)) {
                                                setInvalidRawData(true);
                                                return Promise.reject(t("Please provide suitable storage item data (key-value pair)."));
                                            }
                                            break;
                                        default:
                                            break;
                                    }
                                    setInvalidRawData(false);
                                    return Promise.resolve();
                                }
                            }
                        ],
                        required: true,
                        children: /*#__PURE__*/ _jsx(Input.TextArea, {
                            rows: 10,
                            onChange: ()=>handleCookieParserFormData()
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                name: parsedDataName,
                hidden: true,
                children: /*#__PURE__*/ _jsx(Input, {})
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                shouldUpdate: (prev, next)=>prev.rawTechnicalDefinitions !== next.rawTechnicalDefinitions || prev.technicalDefinitionType !== next.technicalDefinitionType,
                noStyle: true,
                children: ({ getFieldValue })=>{
                    const type = getFieldValue([
                        technicalDefinitionTypeName
                    ]);
                    const rawString = getFieldValue([
                        rawTechnicalDefinitionsName
                    ]);
                    const noneTag = /*#__PURE__*/ _jsx(Tag, {
                        children: t("none")
                    });
                    const shouldDisplaySummary = summary && (summary.skippedHttp?.length > 0 || summary.skippedLocal?.length > 0 || summary.skippedSession?.length > 0);
                    return /*#__PURE__*/ _jsxs(Form.Item, {
                        label: "Import preview",
                        hidden: !type || !rawString || invalidRawData,
                        children: [
                            /*#__PURE__*/ _jsx(Table, {
                                rowKey: "orderPosition",
                                pagination: false,
                                dataSource: previewData,
                                columns: displayColumnDefinition,
                                scroll: {
                                    y: 300
                                }
                            }),
                            shouldDisplaySummary ? /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    paddingTop: "0.5rem",
                                    opacity: 0.7
                                },
                                children: [
                                    type === ETechnicalDefinitionType.Http ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            t("Excluded commonly known default cookies"),
                                            ":",
                                            " ",
                                            summary.skippedHttp?.length > 0 ? /*#__PURE__*/ _jsx(Space, {
                                                size: [
                                                    8,
                                                    8
                                                ],
                                                wrap: true,
                                                children: summary.skippedHttp.map((cookie, index)=>/*#__PURE__*/ _jsx(Tag, {
                                                        children: cookie.name
                                                    }, `${cookie.name}-${index}`))
                                            }) : noneTag
                                        ]
                                    }) : null,
                                    type === ETechnicalDefinitionType.Local ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            t("Excluded commonly known default cookies"),
                                            ":",
                                            " ",
                                            summary.skippedLocal?.length > 0 ? /*#__PURE__*/ _jsx(Space, {
                                                size: [
                                                    8,
                                                    8
                                                ],
                                                wrap: true,
                                                children: summary.skippedLocal.map((cookie, index)=>/*#__PURE__*/ _jsx(Tag, {
                                                        children: cookie.key
                                                    }, `${cookie.key}-${index}`))
                                            }) : noneTag
                                        ]
                                    }) : null,
                                    type === ETechnicalDefinitionType.Session ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            t("Excluded commonly known default cookies"),
                                            ":",
                                            " ",
                                            summary.skippedSession?.length > 0 ? /*#__PURE__*/ _jsx(Space, {
                                                size: [
                                                    8,
                                                    8
                                                ],
                                                wrap: true,
                                                children: summary.skippedSession.map((cookie, index)=>/*#__PURE__*/ _jsx(Tag, {
                                                        children: cookie.key
                                                    }, `${cookie.key}-${index}`))
                                            }) : noneTag
                                        ]
                                    }) : null
                                ]
                            }) : null
                        ]
                    });
                }
            })
        ]
    });
}
export { CookieParserForm };
